/**
 * ReportCard — the surface every block of the report sits on.
 *
 * House style, taken from the product's own screens: a card is *only* a fill
 * and a radius. No border. The page canvas is `background`, a card is
 * `surface1`, and the value step between them is what separates the two —
 * adding a stroke on top of that reads as a foreign accent.
 *
 * `accent` draws a 3px left rule in a semantic tone; it is the one exception,
 * reserved for cards that carry a severity.
 *
 * `hint` puts a HelpHint in the top-right corner rather than beside the
 * title. Glosses belong out of the reading line — the corner is where the
 * product always puts them.
 *
 * Candidate for promotion into the design system (D1).
 *
 * @format
 */

import {forwardRef, type ReactNode, type Ref} from 'react';

import {Box} from '@mui/material';
import {styled, type CSSObject, type Theme} from '@mui/material/styles';

import {spacingPx, type SpacingTokenName} from 'ui/components/layout/spacing';
import {MotionDurationBase, MotionEasingStandard} from 'ui/tokens/ts/motion';
import {RadiusM} from 'ui/tokens/ts/radii';

import {CARD_FILL, CARD_FILL_HOVER} from '../helpers';
import type {PaletteTone} from '../content';
import {HelpHint} from './HelpHint';

export interface ReportCardProps {
  /** Semantic left rule. Omit for a neutral card. */
  accent?: PaletteTone;
  /** Inner padding token. Default `m` (16px) — the product's card padding. */
  padding?: Extract<SpacingTokenName, 'm' | 'l'>;
  /** Gloss rendered as a corner "?" instead of inline beside a label. */
  hint?: ReactNode;
  /** Accessible name for the corner hint trigger. */
  hintLabel?: string;
  /** Drops the fill — for a card that only needs the seam of a SplitCard. */
  transparent?: boolean;
  /**
   * Обрезает содержимое по радиусу карточки. Нужен там, где внутри лежит
   * элемент во всю ширину, доходящий до края: без обрезки его прямые углы
   * торчат из-под скругления.
   */
  clip?: boolean;
  /**
   * Делает карточку кликабельной целиком — корпус становится `button`.
   * Карточка, которая ведёт к документу, не нуждается в кнопке «Открыть»
   * внутри себя: цель размером с карточку промахнуться не даёт, а кнопка
   * повторяет то же действие вторым элементом.
   */
  onClick?: () => void;
  /** Доступное имя кнопки. Имеет смысл только вместе с `onClick`. */
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
}

interface CardGeometry {
  $accent?: PaletteTone;
  $padding: SpacingTokenName;
  $transparent: boolean;
  $clip: boolean;
}

function cardStyles(
  theme: Theme,
  {$accent, $padding, $transparent, $clip}: CardGeometry,
): CSSObject {
  return {
    position: 'relative',
    backgroundColor: $transparent ? 'transparent' : CARD_FILL,
    borderRadius: RadiusM,
    padding: spacingPx($padding),
    minWidth: 0,
    ...($clip ? {overflow: 'clip'} : null),
    ...($accent
      ? {
          borderLeft: `3px solid ${theme.palette[$accent].main}`,
        }
      : null),
  };
}

const Root = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<CardGeometry>(({theme, ...geometry}) => cardStyles(theme, geometry));

/**
 * Кликабельный корпус — отдельным компонентом, а не пропом `as`: у
 * `styled(Box)` типизация идёт по `div`-сигнатуре, и `type`/`aria-*` кнопки
 * на нём не проходят. Геометрия общая, расходиться корпусам нельзя.
 */
const InteractiveRoot = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<CardGeometry>(({theme, ...geometry}) => ({
  ...cardStyles(theme, geometry),
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  border: 'none',
  cursor: 'pointer',
  transition: `background-color ${MotionDurationBase} ${MotionEasingStandard}`,
  '&:hover': {backgroundColor: CARD_FILL_HOVER},
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
}));

const Corner = styled(Box)({
  position: 'absolute',
  top: spacingPx('s'),
  right: spacingPx('s'),
  lineHeight: 0,
});

export const ReportCard = forwardRef<HTMLElement, ReportCardProps>(function ReportCard(
  {
    accent,
    padding = 'm',
    hint,
    hintLabel,
    transparent = false,
    clip = false,
    onClick,
    ariaLabel,
    className,
    children,
  },
  ref,
) {
  const geometry: CardGeometry = {
    $accent: accent,
    $padding: padding,
    $transparent: transparent,
    $clip: clip,
  };

  // Подсказка в углу — сама интерактивный элемент, и внутри кнопки она стала
  // бы вложенной кнопкой. Поэтому кликабельная карточка её не носит: обе
  // роли на одном узле не уживаются.
  const corner = hint != null && !onClick && (
    <Corner>
      <HelpHint label={hintLabel}>{hint}</HelpHint>
    </Corner>
  );

  if (onClick) {
    return (
      <InteractiveRoot
        ref={ref as Ref<HTMLButtonElement>}
        {...geometry}
        type='button'
        onClick={onClick}
        aria-label={ariaLabel}
        className={className}
      >
        {children}
      </InteractiveRoot>
    );
  }

  return (
    <Root ref={ref as Ref<HTMLDivElement>} {...geometry} className={className}>
      {children}
      {corner}
    </Root>
  );
});
